import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openFinanceSlideout } from '../../actions/lister-actions';
import TRANSLATIONS from '../../translations/translations@default.json';
import { getFinanceData } from '../../utilities/finance-utilities';
import FinanceWithChevron from './FinanceWithChevron';
import InterestFreeCreditButton from './InterestFreeCreditButton';
import switch24Pill from '../../assets/switch24Pill.svg';
import { useSwitch24DataHook } from '../../hooks/useSwitch24Data';

const FinanceProposition = ({
	NewDayFinanceQuote,
	FullTitle,
	Promotions: { FinancePromotions } = {},
	PricePodViewModel: { NowPrice, MemberPrice } = {},
	UpfrontPricing = {
		HasData: false,
		HasMemberData: false,
	},
	classes,
	Code,
	ColourId,
	State,
	Switch24Pricing,
}) => {
	const dispatch = useDispatch();

	const { AnnualMembershipFee } = useSelector((state) => state.featureSwitches);
	const { FinancePromotionText } = useSelector(
		(state) => state.featureSwitches
	);
	const { DeliveryPricingInPricePod, UseUpfrontPricingAmounts, UseSwitch24 } =
		useSelector((state) => state.featureSwitches);

	const switch24Props = {
		UseSwitch24,
		Switch24Pricing,
	};
	const isSwitch24Product = useSwitch24DataHook(switch24Props);

	const slideoutPayload = {
		productId: ColourId,
		productSku: Code,
		nowPrice: NowPrice,
		memberPrice: MemberPrice,
		annualMembershipFee: AnnualMembershipFee,
		financePromotionCodes: FinancePromotions
			? FinancePromotions.map((p) => p.PromotionCode)
			: [],
		isInStock: State !== 'OutOfStock',
		memberUpfrontPaymentAmount: UpfrontPricing?.MemberUpfrontPaymentAmount,
		upfrontPaymentAmount: UpfrontPricing?.UpfrontPaymentAmount,
		upfrontPaymentPercentage: UpfrontPricing?.UpfrontPaymentPercentage,
		upfrontPaymentsEnabled: UseUpfrontPricingAmounts,
		switch24Pricing: Switch24Pricing,
	};

	const financeData =
		FinancePromotions?.length > 0
			? getFinanceData(FinancePromotions[0].ImageName, NowPrice)
			: null;

	if (isSwitch24Product) {
		return (
			<>
				<div className=" flex bg-lightblue mt-2 rounded">
					<button
						type="button"
						aria-label="Switch24 Promotion"
						className={`${classes} relative z-10 px-3 w-full bg-transparent`}
						onClick={() => {
							dispatch(
								openFinanceSlideout({
									...slideoutPayload,
								})
							);
						}}
						data-tag-name="finance-switch-24-button"
						data-tag-finance-type="finance-switch-24-button"
						data-tag-product={FullTitle}
						data-testid="finance-switch-24">
						{DeliveryPricingInPricePod && (
							<div className="flex flex-row w-full justify-between items-center text-title-sm pt-2 lg:flex-wrap-reverse">
								AO Finance
								<img
									src={switch24Pill}
									alt="switch24PromoPill"
									style={{ width: '100px', right: '6px' }}
									className="lg:ml-10"
									data-testid="switch-24-pill"
								/>
							</div>
						)}

						<FinanceWithChevron
							NewDayFinanceQuote={NewDayFinanceQuote}
							UpfrontPricing={UpfrontPricing}
							productTitle={FullTitle}
							MemberPrice={MemberPrice}
							NowPrice={NowPrice}
							Code={Code}
							ColourId={ColourId}
							financeType="switch-24"
							financePromotions={FinancePromotions}
							State={State}
							Switch24Pricing={Switch24Pricing}
							isSwitch24Product={isSwitch24Product}
						/>
						<p className="text-caption text-left mb-0 pb-2">
							Other finance options available
						</p>
					</button>
				</div>
			</>
		);
	}
	if (financeData && NewDayFinanceQuote?.PriceIsLessThanMinimum === false) {
		switch (financeData.type) {
			case 'InterestFree':
				return (
					<>
						{DeliveryPricingInPricePod && (
							<div className="text-title-sm mt-4">AO Finance</div>
						)}
						<FinanceWithChevron
							NewDayFinanceQuote={NewDayFinanceQuote}
							UpfrontPricing={UpfrontPricing}
							productTitle={FullTitle}
							MemberPrice={MemberPrice}
							NowPrice={NowPrice}
							Code={Code}
							ColourId={ColourId}
							financeType="interest-free"
							financePromotions={FinancePromotions}
							State={State}
							Switch24Pricing={Switch24Pricing}
							isSwitch24Product={isSwitch24Product}
						/>
						<InterestFreeCreditButton
							financeData={financeData}
							classes={classes}
							fullTitle={FullTitle}
							NowPrice={NowPrice}
							MemberPrice={MemberPrice}
							Code={Code}
							ColourId={ColourId}
							financePromotions={FinancePromotions}
							State={State}
							UpfrontPricing={UpfrontPricing}
							isSwitch24Product={isSwitch24Product}
						/>
					</>
				);
			case 'BuyNowPayLater':
				return (
					<>
						{DeliveryPricingInPricePod && (
							<div className="text-title-sm mt-4">AO Finance</div>
						)}
						<FinanceWithChevron
							NewDayFinanceQuote={NewDayFinanceQuote}
							UpfrontPricing={UpfrontPricing}
							productTitle={FullTitle}
							MemberPrice={MemberPrice}
							NowPrice={NowPrice}
							Code={Code}
							ColourId={ColourId}
							financeType="buy-now-pay-later"
							financePromotions={FinancePromotions}
							Switch24Pricing={Switch24Pricing}
							isSwitch24Product={isSwitch24Product}
						/>
						<button
							type="button"
							aria-label="Buy Now Pay Later Finance Promotion"
							className={`${classes} relative z-10 p-0 w-full mt-2 lg:mt-4 bg-transparent`}
							onClick={() => {
								dispatch(
									openFinanceSlideout({
										...slideoutPayload,
									})
								);
							}}
							data-tag-name="finance-buy-now-pay-later-button"
							data-tag-finance-type="finance-buy-now-pay-later-button"
							data-tag-product={FullTitle}
							data-testid="finance-buy-now-pay-later">
							<div className="flex flex-row items-center bg-steam p-1 rounded-sm text-title-sm text-xs">
								<span className="px-2 flex-grow">{FinancePromotionText}</span>
								<i className="ico ico-chevron-right text-xl text-cta-action" />
							</div>
						</button>
					</>
				);
			default:
				return null;
		}
	}

	// finance messaging
	if (NewDayFinanceQuote?.PriceIsLessThanMinimum === false) {
		return (
			<>
				{DeliveryPricingInPricePod && (
					<div className="text-title-sm mt-4">AO Finance</div>
				)}
				<FinanceWithChevron
					NewDayFinanceQuote={NewDayFinanceQuote}
					UpfrontPricing={UpfrontPricing}
					productTitle={FullTitle}
					MemberPrice={MemberPrice}
					NowPrice={NowPrice}
					Code={Code}
					ColourId={ColourId}
					financeType="finance-instalments"
					financePromotions={FinancePromotions}
					Switch24Pricing={Switch24Pricing}
					isSwitch24Product={isSwitch24Product}
				/>
			</>
		);
	}

	return financeData?.type === 'InterestFree' ? (
		<InterestFreeCreditButton
			financeData={financeData}
			classes={classes}
			fullTitle={FullTitle}
			NowPrice={NowPrice}
			MemberPrice={MemberPrice}
			Code={Code}
			ColourId={ColourId}
			financePromotions={FinancePromotions}
			State={State}
			UpfrontPricing={UpfrontPricing}
		/>
	) : (
		<button
			type="button"
			aria-label="Flexible Credit Finance Promotion"
			className={`${classes} relative z-10 p-0 w-full mt-2 bg-transparent`}
			onClick={() => {
				dispatch(
					openFinanceSlideout({
						...slideoutPayload,
					})
				);
			}}
			data-tag-name="finance-flexible-credit-button"
			data-tag-finance-type="finance-flexible-credit-button"
			data-tag-product={FullTitle}
			data-testid="finance-flexible-credit">
			<div className="flex flex-row items-center bg-white border border-gray-30 border-solid p-2 rounded text-xs text-left">
				<i className="ico ico-finance-gbp text-xl text-cta-main" />
				<span className="px-2 flex-grow text-gray-100">
					{
						TRANSLATIONS.productList.productCard.financeRestyle
							.aoFinanceAvailable
					}
				</span>
				<i className="ico ico-chevron-right text-xl text-cta-action" />
			</div>
		</button>
	);
};

export default FinanceProposition;
